/**
 * Step4MarsConfig Component
 * Step 4: MARS 파티션별 Problem Type/Option 및 RESTART 소스 설정
 */

import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import type { WizardFormData } from './types';
import type { PartitionFormData } from '@/types/project';
import { coveringScopes, svgComponentsForScopes } from '@/types/scope';
import MarsPartitionConfig from './MarsPartitionConfig';
import DiagramPane from './DiagramPane';
import { WIZARD_DIAGRAM_ROW_SX, WIZARD_SIDE_COLUMN_SX } from './wizardLayout';

interface Step4MarsConfigProps {
  data: WizardFormData;
  onChange: (data: Partial<WizardFormData>) => void;
}

const Step4MarsConfig: React.FC<Step4MarsConfigProps> = ({ data, onChange }) => {
  const marsPartitions = useMemo(() => {
    return data.partitions.filter(
      (p) => p.analysisCode === 'MARS'
    );
  }, [data.partitions]);

  const handlePartitionChange = (updated: PartitionFormData) => {
    onChange({
      partitions: data.partitions.map((p) => (p.id === updated.id ? updated : p)),
    });
  };

  // 2·3단계와 같은 자리에 같은 그림 — MARS 파티션이 담당하는 계통을 켠다. 단계를 넘겨도 그림은 그대로 서 있고 오른쪽 열만 바뀐다.
  const highlightedComponents = useMemo(() => svgComponentsForScopes(coveringScopes(marsPartitions)), [marsPartitions]);

  return (
    <Box sx={WIZARD_DIAGRAM_ROW_SX}>
      <DiagramPane highlightedComponents={highlightedComponents}>
        <Typography variant="caption" color="text.secondary">
          MARS 파티션이 담당하는 계통이 하이라이트됩니다
        </Typography>
      </DiagramPane>

      <Box sx={{ ...WIZARD_SIDE_COLUMN_SX, py: 0.5 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>
          MARS Configuration
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
          각 MARS 파티션의 해석 유형과 옵션을 설정합니다.
        </Typography>

        {marsPartitions.map((partition) => (
          <MarsPartitionConfig
            key={partition.id}
            partition={partition}
            onChange={handlePartitionChange}
          />
        ))}
      </Box>
    </Box>
  );
};

export default Step4MarsConfig;
